// Import dependencies
import axios from 'axios';
// Import API configuration
import { AUTH_API_BASE, API_CONFIG, API_BASE } from './config';

// Configure axios defaults
axios.defaults.withCredentials = true; // Always include credentials (cookies) with requests
axios.defaults.timeout = API_CONFIG.timeout; // Set timeout from config

// For development, use window.location.origin (proxy)
// For production, use the configured API_BASE
if (API_CONFIG.isDevelopment) {
  axios.defaults.baseURL = window.location.origin;
} else {
  axios.defaults.baseURL = API_BASE;
}

// Configure axios to handle CORS properly
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Log axios configuration (only in debug mode)
if (API_CONFIG.debugApi) {
  console.log('Axios Configuration:', {
    baseURL: axios.defaults.baseURL,
    withCredentials: axios.defaults.withCredentials,
    timeout: axios.defaults.timeout,
    environment: API_CONFIG.environment
  });
}

// Add request interceptor to include token from localStorage if available
axios.interceptors.request.use(function (config) {
  const token = localStorage.getItem('dashboard_token');
  if (token && config.headers) {
    // Add token to Authorization header
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Ensure withCredentials is set for all requests
  config.withCredentials = true;

  return config;
}, function (error) {
  return Promise.reject(error);
});

// Export all API services
export * from './rpc';
export * from './wallet';
export * from './transaction';
export * from './coins';
export * from './networks';
export * from './coinNetworks';
export * from './fee';
export * from './privateKey';
export * from './withdrawal';
export * from './indexer';
export * from './deposit';

// Import and re-export queue functions from queues.ts
export {
  fetchQueueMetrics,
  fetchQueueHistory,
  fetchDepositQueueStats,
  fetchWithdrawalQueueStats,
  fetchWebhookQueueStats,
  fetchSweepQueueStats,
  fetchSweepHistory,
  fetchFailedWebhookJobs,
  retryWebhookJob,
  retryAllWebhookJobs,
  pauseQueue,
  resumeQueue,
  processQueue,
  clearFailedJobs,
  fetchQueueJobs,
  JobType,
  promoteJob,
  deleteJob,
  changeJobPriority,
  retryJob,
  duplicateJob,
  moveJob,
  fetchFailedTransactionsStats,
  clearFailedTransactionsCache
} from './queues';

/**
 * Check authentication status
 * @returns Authentication status
 */
export const checkAuthentication = async () => {
  const cacheBuster = new Date().getTime();
  console.log('Checking authentication with URL:', `${AUTH_API_BASE}/dashboard/check-auth?_=${cacheBuster}`);

  try {
    const response = await axios.get(`${AUTH_API_BASE}/dashboard/check-auth?_=${cacheBuster}`, {
      withCredentials: true, // Explicitly include credentials
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    console.log('Authentication response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Authentication check failed:', error);
    return { authenticated: false, error: error?.message || 'Authentication check failed' };
  }
};

/**
 * Login to the dashboard
 * @param username Username
 * @param password Password
 * @returns Login result
 */
export const login = async (username: string, password: string) => {
  const response = await axios.post(`${AUTH_API_BASE}/dashboard/login`, {
    username,
    password
  }, {
    withCredentials: true, // Explicitly include credentials
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });
  return response.data;
};

/**
 * Logout from the dashboard
 * @returns Logout result
 */
export const logout = async () => {
  const response = await axios.post(`${AUTH_API_BASE}/dashboard/logout`, {}, {
    withCredentials: true, // Explicitly include credentials
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });
  return response.data;
};
